/**
 * LogoContext
 * -----------
 * Fetches GET /api/settings/logo once on boot and exposes the active logo,
 * favicon, OG image, and product-placeholder URLs across the app.
 *
 * Falls back to the static public-folder files (/logo.png, /favicon.png)
 * when the fetch fails or before any logo has been uploaded via the admin
 * panel — the header and product cards always render something.
 *
 * Separate from PublicSettingsContext (font/feature flags) because:
 *   - It has a single, distinct responsibility.
 *   - The AdminSettingsTab can call refreshLogo() after a logo upload
 *     without also re-fetching font/toggle settings.
 *   - It mirrors the existing PublicSettingsContext refresh pattern cleanly.
 */
import * as React from "react";
import { api } from "../lib/api";

const FALLBACKS = {
  logoUrl: "/logo.png",
  faviconUrl: "/favicon.png",
  ogImageUrl: "/logo.png",
  productPlaceholderUrl: "/logo.png",
};

interface LogoState {
  logoUrl: string;
  faviconUrl: string;
  ogImageUrl: string;
  productPlaceholderUrl: string;
}

interface LogoCtx extends LogoState {
  refreshLogo: () => Promise<void>;
}

const LogoContext = React.createContext<LogoCtx>({
  ...FALLBACKS,
  refreshLogo: async () => {},
});

export function LogoProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<LogoState>(FALLBACKS);

  const refreshLogo = React.useCallback(async () => {
    try {
      const r = await api.get<{
        logo_url: string | null;
        favicon_url: string | null;
        og_image_url: string | null;
        product_placeholder_url: string | null;
      }>("/settings/logo");
      setState({
        logoUrl: r.data.logo_url || FALLBACKS.logoUrl,
        faviconUrl: r.data.favicon_url || FALLBACKS.faviconUrl,
        ogImageUrl: r.data.og_image_url || FALLBACKS.ogImageUrl,
        productPlaceholderUrl:
          r.data.product_placeholder_url || FALLBACKS.productPlaceholderUrl,
      });
    } catch {
      setState(FALLBACKS);
    }
  }, []);

  React.useEffect(() => {
    refreshLogo();
  }, [refreshLogo]);

  return (
    <LogoContext.Provider value={{ ...state, refreshLogo }}>
      {children}
    </LogoContext.Provider>
  );
}

export function useLogoContext(): LogoCtx {
  return React.useContext(LogoContext);
}
